import * as React from 'react'
import { createPortal } from 'react-dom'

type Props = {
  text: string
  children: React.ReactNode
}

/**
 * Dark styled tooltip (same look as the shared `Tooltip`) rendered through a
 * portal with `position: fixed`. Inside the scrolling business-trips table an
 * in-flow absolutely-positioned tooltip is painted behind the next row and
 * clipped by the scroll container; portaling to `document.body` escapes both.
 */
const FailureTooltip: React.FC<Props> = ({ text, children }) => {
  const triggerRef = React.useRef<HTMLSpanElement>(null)
  const [coords, setCoords] = React.useState<{ x: number; y: number } | null>(
    null,
  )

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect) {
      setCoords({ x: rect.left + rect.width / 2, y: rect.bottom + 8 })
    }
  }
  const hide = () => setCoords(null)

  return (
    <span
      ref={triggerRef}
      className="tw-inline-flex tw-cursor-help"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {coords &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              left: coords.x,
              top: coords.y,
              transform: 'translateX(-50%)',
              zIndex: 9999,
            }}
            className="tw-pointer-events-none tw-select-none tw-whitespace-nowrap tw-rounded tw-bg-neutral-900 tw-p-1 tw-opacity-90"
          >
            <p className="tw-text-sm tw-text-secondary">{text}</p>
            <div className="tw-absolute tw-left-1/2 tw-top-[-4px] tw-h-2 tw-w-2 tw--translate-x-1/2 tw-rotate-45 tw-bg-neutral-900" />
          </div>,
          document.body,
        )}
    </span>
  )
}

export default FailureTooltip
